import { useMount } from 'ahooks';
import { uniqueId } from 'lodash-es';
import React from 'react';
import { shallowEqual } from 'react-redux';
import usePropsSelector from '../../../../store/propsStore/usePropsSelector';
import { getSpecificParent } from '../../../../utils/dom';
import TreeDataController from './TreeDataController';
import { TTreeProps, TDataNode, TId } from './types';
import { selectAllNodesBetweenTwoNodes } from './util';

interface IUseTreeData<
  NodeProps extends Record<string, unknown> = Record<string, unknown>,
  NodeType extends TDataNode<NodeProps> = TDataNode<NodeProps>,
> {
  name: string;
  treeProps: TTreeProps<NodeProps, NodeType>;
}

export default function useTreeData<
  NodeProps extends Record<string, unknown> = Record<string, unknown>,
  NodeType extends TDataNode<NodeProps> = TDataNode<NodeProps>,
>({ name, treeProps }: IUseTreeData<NodeProps, NodeType>) {
  const handler = React.useMemo(
    () =>
      new TreeDataController<NodeProps, NodeType>(name, {
        hideEmptyFolders: treeProps.hideEmptyFolders,
        isMultiple: treeProps.isMultiple,
        onExpand: treeProps.onExpand,
        onLoadData: treeProps.onLoadData,
        disableSelection: treeProps.disableSelection,
        selectionMode: treeProps.selectionMode,
        disableExpand: treeProps.disableExpand,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useMount(() => {
    treeProps?.initialNodes?.forEach((current) => handler.append(current));
  });

  const data = usePropsSelector('root', {
    comparator: shallowEqual,
    propsStore: handler.propsStore,
    selector: (current) => ({
      children: current.children ?? [],
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
      forceUpdate: (current as any).forceUpdate as number,
    }),
  });

  const keyHandlerId = React.useMemo(() => `keyHandler${uniqueId()}`, []);
  const keyHandlerRef = React.useRef<HTMLDivElement | null>(null);

  const focusOnNode = React.useCallback(
    (key: TId, retry = 3) => {
      if (keyHandlerRef.current) {
        const focusElement = keyHandlerRef.current.querySelector(
          `[data-key="${key}"]`,
        ) as HTMLElement;
        keyHandlerRef.current?.focus();

        if (focusElement) {
          const actualFocusElement = (
            treeProps.focusGetter ??
            ((liElement: HTMLElement) =>
              liElement.querySelector(
                ':scope > .tree__nodeItemLabel',
              ) as HTMLElement)
          )(focusElement);

          const nodeProps = handler.propsStore.getFieldProps(key);

          if (!treeProps.focusGetter || !nodeProps.labelRenderer)
            actualFocusElement.classList.add('focused');
          actualFocusElement.scrollIntoView({
            inline: 'nearest',
            block: 'nearest',
          });
        } else if (retry > 0) setTimeout(() => focusOnNode(key, retry - 1), 30);
      }
    },
    [handler.propsStore, treeProps.focusGetter],
  );

  React.useEffect(() => {
    const unsuscribe1 = handler.on('mustFocus', (node) => focusOnNode(node));
    const unsuscribe2 = handler.on('onArrowUpOnFirstElement', () =>
      treeProps.onArrowUpOnFirstElement?.(),
    );

    return () => {
      unsuscribe1();
      unsuscribe2();
    };
  }, [focusOnNode, handler, treeProps]);

  return {
    data,
    handler,
    keyHandler: {
      id: keyHandlerId,
      onKeyDown: React.useCallback(
        (ev: React.KeyboardEvent) => {
          if (ev.key === 'Enter') {
            const key = handler.state.focusedNode;
            if (key) {
              const nodeProps = handler.propsStore.getFieldProps(key);
              treeProps.onNodeClick?.(ev, nodeProps);
            }
          } else {
            handler.handleKey(ev);
          }
        },
        [handler, treeProps],
      ),
      onMouseDown: React.useCallback(
        (ev: React.MouseEvent) => {
          const previousFocused = handler.state.focusedNode;
          if (
            previousFocused !== null &&
            ev.shiftKey &&
            handler.configuration.isMultiple
          )
            ev.preventDefault();
        },
        [handler.configuration.isMultiple, handler.state.focusedNode],
      ),
      onClick: React.useCallback(
        (ev: React.MouseEvent) => {
          const node = getSpecificParent(
            ev.target as HTMLElement,
            (current) => !!current.getAttribute('data-key'),
          );
          if (node) {
            const previousFocused = handler.state.focusedNode;
            const key = node.getAttribute('data-key') as string;

            const nodeProps = handler.propsStore.getFieldProps(key);
            treeProps.onNodeClick?.(ev, nodeProps);

            if (
              previousFocused !== null &&
              ev.shiftKey &&
              handler.configuration.isMultiple
            ) {
              selectAllNodesBetweenTwoNodes(
                handler,
                previousFocused,
                key,
                true,
              );
            } else {
              handler.setSelectedNodesByClickEvent(ev);
            }

            handler.setFocusedNode(key);
            if (key) focusOnNode(key);
          }
        },
        [focusOnNode, handler, treeProps],
      ),
      ref: keyHandlerRef,
    },
  };
}
